import styled from "styled-components";
import { useToaster } from "@hellocontento/maillard";
import React, { useMemo, useState, useCallback } from "react";

import { format } from "utils/date";
import Button from "components/common/Button";
import Loader from "components/common/loading/Loader";
import { ToolTip } from "components/schedule/common/styles";
import { Body7, Headline6 } from "components/common/styles";

const IdeaCardContainer = styled.div`
  width: 306px;
  height: 320px;
  display: flex;
  padding: 10px;
  align-items: center;
  justify-content: center;
  background: transparent;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 286px;
  height: 300px;
  overflow: hidden;

  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.grey07};
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.shadow04};
`;

const Cover = styled.div<{ src: string }>`
  width: 100%;
  height: 164px;
  background: url(${props => props.src}) center center/cover no-repeat lightgray;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  padding: 3px 30px;
  align-items: flex-start;
  width: 100%;
`;

const Title = styled(Headline6)`
  font-weight: 700;
  position: relative;
  max-width: 226px;

  > span {
    display: inline-block;
    max-width: 206px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${props => props.theme.colors.darkBlack};
  }

  > i {
    position: absolute;
    right: -20px;
    font-size: 15px;
    vertical-align: middle;
    color: ${props => props.theme.colors.grey06};
  }
`;

const DateRange = styled(Body7)`
  display: flex;
  padding: 9px 0px;
  line-height: 125%;
  color: ${props => props.theme.colors.grey06};

  > i {
    margin-right: 10px;
    color: inherit;
    font-size: 15px;
    vertical-align: middle;
  }
`;

const Actions = styled.div`
  display: flex;
  padding: 0px 30px;
  margin-bottom: 19px;
  justify-content: flex-start;
  width: 100%;
`;

interface ITopicalCalendarCard {
  id: string;
  coverImg: string;
  title: string;
  tooltip: string;
  startDate: Date;
  endDate: Date;
  status: string;
  isVoted: boolean;
  isSubscribed: boolean;
  handleButtonAction: (
    topicalCalendarId: string,
    calendarName: string,
    status: string,
    upvote: boolean,
    location: string
  ) => void;
}

const TopicalCalendarCard: React.FC<ITopicalCalendarCard> = ({
  id,
  coverImg,
  title,
  tooltip,
  startDate,
  endDate,
  status,
  isVoted,
  isSubscribed,
  handleButtonAction
}) => {
  const addToast = useToaster();
  const [isActionRunning, setIsActionRunning] = useState<boolean>(false);

  const buttonStatus = useMemo(() => {
    if (status === "live" && !isSubscribed) {
      return "unsubscribed";
    } else if (status === "live" && isSubscribed) {
      return "subscribed";
    } else if (status === "voting" && isVoted) {
      return "voted";
    } else {
      return "unvoted";
    }
  }, [status, isVoted, isSubscribed]);

  const buttonState = useMemo(() => {
    if (buttonStatus === "unsubscribed") {
      return {
        variant: "primary",
        text: "Subscribe"
      };
    } else if (buttonStatus === "subscribed") {
      return {
        variant: "danger",
        text: "Unsubscribe"
      };
    } else if (buttonStatus === "voted") {
      return {
        variant: "success",
        text: "Vote Registered!"
      };
    } else {
      return {
        variant: "secondary",
        text: "Vote For This Calendar"
      };
    }
  }, [buttonStatus]);

  const handleAction = useCallback(async () => {
    if (buttonStatus !== "voted") {
      try {
        setIsActionRunning(true);

        await handleButtonAction(
          id,
          title,
          status,
          status === "voting" ? !isVoted : !isSubscribed,
          "ideas"
        );
      } catch (error) {
        addToast((error as Error).message, "error");
      } finally {
        setIsActionRunning(false);
      }
    }
  }, [
    id,
    title,
    status,
    isVoted,
    addToast,
    buttonStatus,
    isSubscribed,
    handleButtonAction
  ]);

  return (
    <IdeaCardContainer>
      <Wrapper>
        <Cover src={coverImg} />
        <Details>
          <Title>
            <span>{title}</span>
            <i
              className="icon-help"
              data-tip={tooltip}
              data-for="info-tooltip"
            />
            <ToolTip id="info-tooltip" place="top" className="tooltip" />
          </Title>
          <DateRange>
            <i className="icon-calendar" />{" "}
            {status === "voting"
              ? `Coming Soon!`
              : `
            ${format(startDate, "topical_calendar")} - ${format(
              endDate,
              "topical_calendar"
            )}
            `}
          </DateRange>
        </Details>
        <Actions>
          {/* @ts-ignore */}
          <Button
            size="xl"
            variant={buttonState.variant}
            onClick={handleAction}
            disabled={isActionRunning}
          >
            {isActionRunning && (
              <span
                style={{
                  position: isActionRunning ? "absolute" : "unset",
                  left: "calc(50% - 12px)"
                }}
              >
                <Loader size={24} />
              </span>
            )}
            <span
              style={{
                visibility: isActionRunning ? "hidden" : "visible"
              }}
            >
              {buttonState.text}
            </span>
          </Button>
        </Actions>
      </Wrapper>
    </IdeaCardContainer>
  );
};

export default TopicalCalendarCard;
